"use client";

import { useEffect, useMemo, useState } from "react";
import { Button, Typography } from "@repo/ui";
import Link from "next/link";
import { Cookie } from "lucide-react";
import clsx from "clsx";
import { getCookie, setCookie } from "cookies-next/client";

const COOKIE_CONSENT_KEY = "spoclearn-cookie-consent";
const ONE_YEAR_IN_MS = 1000 * 60 * 60 * 24 * 365;

type PreferenceKey = "necessary" | "analytics" | "marketing";

type CookiePreferences = Record<PreferenceKey, boolean>;

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const preferenceCopy: Array<{
  key: PreferenceKey;
  title: string;
  description: string;
  locked?: boolean;
}> = [
  {
    key: "necessary",
    title: "Essential Cookies",
    description:
      "Required for core functionality such as secure logins, shopping cart preferences, and loading the site correctly.",
    locked: true,
  },
  {
    key: "analytics",
    title: "Performance & Analytics",
    description:
      "Help us understand how visitors use Spoclearn so we can measure what works well and improve future experiences.",
  },
  {
    key: "marketing",
    title: "Personalization & Marketing",
    description:
      "Allow us to offer tailored content, relevant recommendations, and promotions across Spoclearn channels.",
  },
];

const CookieConsent = () => {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    setIsMounted(true);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const consentCookie = getCookie(COOKIE_CONSENT_KEY);

    if (consentCookie) {
      try {
        const parsedConsent = JSON.parse(String(consentCookie));
        setPreferences({
          ...DEFAULT_PREFERENCES,
          ...parsedConsent,
        });
        return;
      } catch {
        // Fall back to showing the banner if the existing cookie cannot be parsed.
      }
    }

    timer = setTimeout(() => {
      setIsVisible(true);
    }, 1200);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  const persistConsent = (value: CookiePreferences) => {
    const expires = new Date(Date.now() + ONE_YEAR_IN_MS);
    setCookie(COOKIE_CONSENT_KEY, JSON.stringify(value), {
      expires,
      sameSite: "lax",
      secure: process.env.NODE_ENV === "production",
      path: "/",
    });
  };

  const acceptAll = () => {
    const updated = { ...preferences, analytics: true, marketing: true };
    setPreferences(updated);
    persistConsent(updated);
    setIsVisible(false);
  };

  const declineOptional = () => {
    setPreferences(DEFAULT_PREFERENCES);
    persistConsent(DEFAULT_PREFERENCES);
    setIsVisible(false);
  };

  const saveSelection = () => {
    persistConsent(preferences);
    setIsVisible(false);
  };

  const togglePreference = (key: PreferenceKey) => {
    if (key === "necessary") return;
    setPreferences((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const preferenceCards = useMemo(() => preferenceCopy, []);

  if (!isMounted || !isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 pointer-events-none">
      <div className="pointer-events-auto px-4 pb-4">
        <section className="max-w-5xl overflow-hidden rounded-3xl border border-gray-300 bg-white/95 shadow-2xl backdrop-blur">
          <div className="flex flex-col gap-5 p-5 sm:p-8 md:flex-row md:items-start">
            <div className="md:w-3/5">
              <div className="mb-3 flex items-center gap-3 text-primary-main">
                <span className="rounded-2xl bg-primary-main/10 p-2 text-primary-main">
                  <Cookie className="size-5" />
                </span>
                <Typography
                  variant="h5"
                  className="text-primary-dark"
                  weight="bold"
                >
                  We value your privacy
                </Typography>
              </div>
              <Typography variant="body1" className="text-slate-600">
                Spoclearn uses cookies to make sure our website works as
                expected, to understand how it is used, and to deliver
                personalized experiences. You can accept all cookies or review
                your choices. Learn more in our{" "}
                <Link
                  href="/privacy-policy/"
                  className="font-semibold text-primary-main underline decoration-primary-main/30 underline-offset-4 transition hover:text-primary-dark"
                >
                  Privacy Policy
                </Link>
                .
              </Typography>
            </div>

            <div className="flex w-full flex-col gap-3 md:w-2/5">
              <Button
                color="primary"
                fullWidth
                onClick={acceptAll}
                className="shadow-md shadow-primary-main/30"
              >
                Accept all cookies
              </Button>
              <Button
                variant="outlined"
                color="gray"
                fullWidth
                onClick={declineOptional}
                className="border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
              >
                Only essential
              </Button>
              <Button
                variant="text"
                color="primary"
                fullWidth
                type="button"
                onClick={() => setShowPreferences((state) => !state)}
              >
                {showPreferences ? "Hide preferences" : "Manage preferences"}
              </Button>
            </div>
          </div>

          {showPreferences && (
            <div className="border-t border-slate-100 bg-slate-50/70 p-5 sm:p-8">
              <div className="mb-4 flex items-center justify-between gap-2">
                <Typography variant="subtitle1" className="text-slate-900">
                  Customize your cookie preferences
                </Typography>
                <Button
                  color="primary"
                  size="small"
                  onClick={saveSelection}
                  className="px-4"
                >
                  Save selection
                </Button>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                {preferenceCards.map((preference) => (
                  <article
                    key={preference.key}
                    className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div>
                        <Typography
                          variant="subtitle2"
                          className="text-slate-900"
                          weight="bold"
                        >
                          {preference.title}
                        </Typography>
                        {preference.locked && (
                          <span className="inline-flex items-center rounded-full bg-primary-main/10 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-primary-main">
                            Always on
                          </span>
                        )}
                      </div>

                      <button
                        type="button"
                        role="switch"
                        aria-checked={preferences[preference.key]}
                        aria-label={`Toggle ${preference.title}`}
                        onClick={() => togglePreference(preference.key)}
                        disabled={preference.locked}
                        className={clsx(
                          "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border transition-all duration-300",
                          preference.locked
                            ? "cursor-not-allowed border-slate-200 bg-slate-100"
                            : preferences[preference.key]
                              ? "cursor-pointer border-primary-main bg-primary-main"
                              : "cursor-pointer border-slate-200 bg-slate-200"
                        )}
                      >
                        <span
                          className={clsx(
                            "inline-block h-[18px] w-[18px] rounded-full bg-white shadow-md transition-all duration-300",
                            preferences[preference.key]
                              ? "translate-x-[18px]"
                              : "translate-x-[2px]"
                          )}
                        />
                      </button>
                    </div>
                    <Typography variant="body2" className="text-slate-600">
                      {preference.description}
                    </Typography>
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CookieConsent;
